import { useEffect, useState } from 'react';
import useUserAccount from '../../../../hooks/userAccountHook';
import { useQuery } from '@tanstack/react-query';
import useModal from '../../../../hooks/modalHook';
import { COMPANY_TYPES } from '../../../../resources/enums';
import Page from '../../../../components/PageWrapper';
import { capitalizeSentence } from '../../../../utils/miscFunctions';
import usePartner from '../../../../hooks/partnerHook';
import TableFilter from '../../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ITableFilterItem } from '../../../../resources/interfaces';
import usePolicy from '../../../../hooks/policyHook';
import SettlementsTable from './components/SettlementsTable';
import useSettlements from '../../../../hooks/settlementHook';
import SettlementsOverviewPanel from './components/SettlementsOverviewPanel';
import { ISelectDropdownItem } from '../../../../components/form-control/SelectDropdown';
import { Company } from '../../../../models/company/Company';
import { useSearchParams } from 'react-router-dom';


const SettlementsView = () => {
    const page = 'settlement';
    const { user } = useUserAccount();
    const { ModalComponent } = useModal();
    const { fetchAllCompaniesByTypes } = usePartner();
    const { POLICY_FILTERS } = usePolicy()
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const { fetchSettlementMetrics, } = useSettlements({})
    const [searchParams, setSearchParams] = useSearchParams();
    const [invalidateQuery, setInvalidateQuery] = useState<boolean>(false);

    const { data: insurersList } = useQuery({
        queryKey: ['companies'],
        queryFn: async () => {
            try {
                const insurers = await fetchAllCompaniesByTypes([COMPANY_TYPES.INSURER]);
                return insurers;
            } catch (error) {
                console.error('Error fetching insurers:', error);
                return [];
            }
        },
    });

    const { data: brokersList } = useQuery({
        queryKey: ['brokers'],
        queryFn: () => fetchAllCompaniesByTypes([COMPANY_TYPES.BROKER]),
    });

    const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
    const toDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.to;
    const fromDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.from;
    const isPaid = searchParams.get('payment_status') ? searchParams.get('payment_status') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'payment_status')?.value;
    const isApproved = searchParams.get('approved_status') ? searchParams.get('approved_status') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'approved_status')?.value;
    const reviewStage = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'review_stage')?.value;
    const insurerId = searchParams.get('insurer') ? searchParams.get('insurer') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'insurer')?.value || (user.isInsurer() && user?.company?.id);
    const brokerId = searchParams.get('broker') ? searchParams.get('broker') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'broker')?.value || (user.isBroker() && user?.company?.id);

    const { data: settlementMetrics, isLoading: isLoadingSettlementMetrics } = useQuery({
        queryFn: async () => {
            const response = await fetchSettlementMetrics(brokerId, fromDate, toDate, insurerId, isApproved, isPaid);
            return response;
        },
        queryKey: ['getSettlementMetrics', brokerId, fromDate, toDate, insurerId, isApproved, isPaid, reviewStage],
    });

    useEffect(() => {
        setTableFilters(getDefaultFilters());
    }, [insurersList, brokersList]);

    function getDefaultFilters() {
        const filters: ITableFilterItem[] = [
            {
                name: 'create_date',
                value: '',
                type: 'dateRange',
                placeholder: 'Select start date',
                label: 'Date Range'
            },
            {
                name: 'payment_status',
                value: '',
                list: [{
                    name: 'Paid',
                    value: 'true'
                },
                {
                    name: 'Not paid',
                    value: 'false'
                },
                ],
                type: 'select',
                label: 'Payment Status',
                placeholder: 'select status'
            }, {
                name: 'approved_status',
                value: '',
                list: [{
                    name: 'Approve',
                    value: 'true'
                },
                {
                    name: 'Not approved',
                    value: 'false'
                },
                ],
                type: 'select',
                label: 'Approved Status',
                placeholder: 'select status'
            }, {
                name: 'review_stage',
                value: '',
                list: POLICY_FILTERS,
                type: 'select',
                label: 'Status',
                placeholder: 'select status'
            }
        ];

        if (user.isInternalAdmin() || user.isInsurer()) {
            filters.unshift({
                name: 'broker',
                value: '',
                list: brokersList?.map(
                    (broker: any) =>
                    ({
                        name: broker.name,
                        value: broker.id
                    } as ISelectDropdownItem)
                ),
                type: 'select',
                label: 'From broker',
                placeholder: 'select broker'
            })
        }


        if (user.isBroker() || user.isInternalAdmin()) {
            filters.unshift({
                name: 'insurer',
                value: '',
                list: insurersList?.map((insurer: Company) => ({
                    name: insurer.name,
                    value: insurer.id,
                })),
                showSearch: true,
                type: 'select',
                label: 'To insurer',
                placeholder: 'select insurer'
            })
        }


        return filters;
    }

    const getFilter = () => {
        return (
            <div className="flex flex-col items-center gap-2 max-w-[10rem]">
                <TableFilter
                    openFilter={openFilter}
                    onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
                    selectedFilters={tableFilters}
                    content={
                        <TableFilterForm
                            filters={tableFilters}
                            getValues={(filters: ITableFilterItem[]) => {
                                setTableFilters(filters);
                                setOpenFilter(false);

                            }}
                            onResetFilters={() => setTableFilters(getDefaultFilters())}
                        />
                    }
                />
            </div>
        )
    }

    const handlePayoutCardClick = (isPaid: boolean | null) => {
        isPaid === null ? setInvalidateQuery(true) : setTableFilters(prev =>
            prev.map(item =>
                item.name === "payment_status"
                    ? { ...item, value: isPaid, value2: isPaid ? 'Paid' : 'Not paid' }
                    : item
            )
        );
        if (isPaid === null) {
            setTableFilters(prev =>
                prev.map(item =>
                    item.name === "payment_status"
                        ? { ...item, value: '', value2: '' }
                        : item
                )
            )
        }
    };

    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg">
                <div className="flex items-center justify-between">

                    {/* {showTitleSection && ( */}
                    <div className="">
                        <div className="inline-flex items-center gap-2 mb-2">
                            <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                            <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
                        </div>

                        <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                            View a list of all {page}s and their details{' '}
                        </p>
                    </div>
                    {/* )} */}

                    {/* Filters & Actions */}
                    <div className="flex items-center space-x-3">
                        {getFilter()}
                    </div>
                </div>

                <div className="my-6">
                    <SettlementsOverviewPanel data={settlementMetrics} isLoading={isLoadingSettlementMetrics} onCardClick={(isPaid: boolean | null) => handlePayoutCardClick(isPaid)} />
                </div>


                <div className="">
                    <SettlementsTable
                        showTitleSection={false}
                        selectedBrokerFromParent={brokerId}
                        selectedInsurerFromParent={insurerId}
                        fromDate={fromDate}
                        toDate={toDate}
                        isPaid={isPaid}
                        isApproved={isApproved}
                        reviewStage={reviewStage}
                        tableFilters={tableFilters}
                        setTableFilters={setTableFilters}
                        invalidateQuery={invalidateQuery}
                    />
                </div>

                <ModalComponent />
            </div>
        </Page>
    );
};

export default SettlementsView;
